import { SidebarItems1 } from '@/layouts/data';
import { AgencyType, IAccount, IUser, Role } from '@/models';

import {
  hasAnyOfTargetRoles,
  isAccountAdmin,
  isAgencyEnabledJMS,
  isAgencyTypeRole,
  isAnyTargetRole,
  isTargetRole,
} from './service.role';

type IValidatorPayload = {
  user?: IUser;
  account?: IAccount;
};

interface IRouteValidator {
  path: string;
  validator: (payload: IValidatorPayload) => boolean | undefined;
}

const ProtectedRoutes: IRouteValidator[] = [
  {
    path: '/my-cfs',
    validator: ({ user }) => isTargetRole(user as IUser, Role.FIRST_RESPONDER),
  },
  {
    path: '/map-view',
    validator: ({ user }) => isTargetRole(user as IUser, Role.FIRST_RESPONDER),
  },
  {
    path: '/admin',
    validator: ({ user }) =>
      isTargetRole(user as IUser, Role.ACCOUNT_ADMIN) ||
      isTargetRole(user as IUser, Role.AGENCY_ADMIN),
  },
  {
    path: '/cfs',
    validator: ({ user }) =>
      hasAnyOfTargetRoles(user as IUser, [Role.DISPATCHER, Role.CFS_VIEW_ONLY]),
  },
  {
    path: '/cfs-active',
    validator: ({ user }) =>
      hasAnyOfTargetRoles(user as IUser, [Role.DISPATCHER, Role.CFS_VIEW_ONLY]),
  },
  {
    path: '/ncic-search',
    validator: ({ user }) => isTargetRole(user as IUser, Role.NCIC_AUTHORIZED),
  },
  {
    path: '/analytics',
    validator: ({ account }) => account?.settings.demoMode,
  },
  {
    path: '/cases',
    validator: ({ user }) => {
      if (!user) return false;
      return (
        isAgencyTypeRole(user, Role.AGENCY_ADMIN, AgencyType.POLICE) ||
        isAnyTargetRole(user, [
          Role.KASE_APPROVER,
          Role.RMS_CASES_FULL_ACCESS,
          Role.RMS_CASES_VIEW,
        ])
      );
    },
  },
  {
    path: '/fire-cases',
    validator: ({ user }) => {
      if (!user) return false;
      return isAgencyTypeRole(user, Role.FIRE_RMS, AgencyType.FIRE);
    },
  },
  {
    path: '/master-index',
    validator: ({ user }) =>
      hasAnyOfTargetRoles(user as IUser, [
        Role.DISPATCHER,
        Role.AGENCY_ADMIN,
        Role.RMS_CASES_FULL_ACCESS,
        Role.RMS_CASES_VIEW,
        Role.KASE_APPROVER,
      ]) ||
      (isTargetRole(user as IUser, Role.FIRST_RESPONDER) &&
        hasAnyOfTargetRoles(user as IUser, [
          Role.MASTER_INDEX_ADDRESSES,
          Role.MASTER_INDEX_PERSONS,
          Role.MASTER_INDEX_VEHICLES,
        ])),
  },
  {
    path: '/vendors',
    validator: ({ account }) => account?.settings.features?.isEnabledVendors,
  },
  {
    path: '/inventory-locations',
    validator: ({ account }) =>
      account?.settings.features?.isEnabledInventoryLocation,
  },
  {
    path: '/tasks',
    validator: ({ user }) =>
      isAccountAdmin(user as IUser) ||
      isTargetRole(user as IUser, Role.AGENCY_ADMIN) ||
      isTargetRole(user as IUser, Role.ACCESS_TASKS),
  },
  {
    path: '/arrests',
    validator: ({ user }) => {
      if (!user) return false;
      return (
        isAgencyTypeRole(user, Role.AGENCY_ADMIN, AgencyType.POLICE) ||
        isAnyTargetRole(user, [
          Role.KASE_APPROVER,
          Role.RMS_CASES_FULL_ACCESS,
          Role.RMS_CASES_VIEW,
        ])
      );
    },
  },
  {
    path: '/jms',
    validator: ({ user, account }) => {
      if (!user) return false;
      return (
        isAgencyTypeRole(user, Role.JAIL_OFFICER, AgencyType.POLICE) &&
        account?.settings.features?.isEnabledJSMModule &&
        isAgencyEnabledJMS(user)
      );
    },
  },
  {
    path: '/evidence',
    validator: ({ user }) =>
      isTargetRole(user as IUser, Role.RMS_CASES_FULL_ACCESS) ||
      isTargetRole(user as IUser, Role.RMS_CASES_VIEW),
  },
  {
    path: '/tools/sex-offenders',
    validator: ({ user }) =>
      isAnyTargetRole(user as IUser, [
        Role.TOOLS_MANAGER,
        Role.FIRST_RESPONDER,
        Role.DISPATCHER,
      ]),
  },
  {
    path: '/tools/cameras',
    validator: ({ user }) =>
      isAnyTargetRole(user as IUser, [
        Role.TOOLS_MANAGER,
        Role.FIRST_RESPONDER,
        Role.DISPATCHER,
      ]),
  },
  {
    path: '/tools/bolo',
    validator: ({ user }) =>
      isAnyTargetRole(user as IUser, [
        Role.TOOLS_MANAGER,
        Role.FIRST_RESPONDER,
        Role.DISPATCHER,
      ]),
  },
  {
    path: '/tools/complaints',
    validator: ({ user }) =>
      isAnyTargetRole(user as IUser, [
        Role.TOOLS_MANAGER,
        Role.FIRST_RESPONDER,
        Role.DISPATCHER,
      ]),
  },
  {
    path: '/tools/permit',
    validator: ({ user }) =>
      isAnyTargetRole(user as IUser, [
        Role.TOOLS_MANAGER,
        Role.FIRST_RESPONDER,
        Role.DISPATCHER,
      ]),
  },
  {
    path: '/tools/towing-management',
    validator: ({ user }) =>
      isAnyTargetRole(user as IUser, [
        Role.TOOLS_MANAGER,
        Role.FIRST_RESPONDER,
        Role.DISPATCHER,
      ]),
  },
  {
    path: '/tools/calendar-scheduler',
    validator: ({ user }) =>
      isAnyTargetRole(user as IUser, [
        Role.TOOLS_MANAGER,
        Role.FIRST_RESPONDER,
        Role.DISPATCHER,
      ]),
  },
  {
    path: '/tools/domestic-incident-reports',
    validator: ({ user }) =>
      isAnyTargetRole(user as IUser, [
        Role.ACCOUNT_ADMIN,
        Role.RMS_CASES_FULL_ACCESS,
        Role.RMS_CASES_VIEW,
        Role.AGENCY_ADMIN,
        Role.KASE_APPROVER,
      ]),
  },
  {
    path: '/tools/wanted-person',
    validator: ({ user }) =>
      isAnyTargetRole(user as IUser, [
        Role.WANTED_PERSON_VIEW,
        Role.WANTED_PERSON_VIEW_ADD_ATTEMPT,
        Role.WANTED_PERSON_EDIT,
      ]),
  },
  {
    path: '/tools/wanted-person/edit',
    validator: ({ user }) =>
      isTargetRole(user as IUser, Role.WANTED_PERSON_EDIT),
  },
];

// Check if user has permission to access given route.
// Match a route's path against the URL using equals-or-prefix-with-separator semantics, NOT
// substring containment. Substring containment caused in-case sub-routes (e.g. /cases/:id/arrests)
// to inherit the standalone /arrests validator, redirecting VIEW users away from case tabs.
const matchesRoute = (urlPath: string, routePath: string): boolean =>
  urlPath === routePath || urlPath.startsWith(`${routePath}/`);

export const validateRoute = (
  path: string,
  user?: IUser,
  account?: IAccount,
) => {
  const validators = ProtectedRoutes.filter((route) =>
    matchesRoute(path, route.path),
  ).map(({ validator }) => validator);
  if (validators.length) {
    return validators.every((validator) => validator({ user, account }));
  } else {
    return true;
  }
};

// Check if a submenu item should be visible based on role
export const shouldShowSubmenuItem = (
  path: string,
  user?: IUser,
  account?: IAccount,
): boolean => {
  if (!user) return false;

  const isDemoMode = account?.settings?.demoMode === true;
  const isDispatcher = isTargetRole(user, Role.DISPATCHER);
  const isFirstResponder = isTargetRole(user, Role.FIRST_RESPONDER);

  // Always handle wanted-person logic separately
  if (path === '/tools/wanted-person') {
    return (
      isTargetRole(user, Role.WANTED_PERSON_VIEW) ||
      isTargetRole(user, Role.WANTED_PERSON_VIEW_ADD_ATTEMPT) ||
      isTargetRole(user, Role.WANTED_PERSON_EDIT)
    );
  }

  if (isDemoMode && path.startsWith('/tools/')) {
    return true;
  }

  if (path.startsWith('/tools/vehicle-maintenance')) {
    return true;
  }
  if (path.startsWith('/tools/building-inspection')) {
    return true;
  }

  if (
    (isDispatcher || isFirstResponder) &&
    path === '/tools/location-markers'
  ) {
    return true;
  }

  // Default: allow all other submenu items
  return true;
};

// Filter sidebar items that user has permission to access
export const getAllowedSidebarItems = (user: IUser, account?: IAccount) => {
  // Filter top-level menu items based on route validation
  const filteredItems = SidebarItems1.filter(({ link }) =>
    validateRoute(link, user, account),
  );

  // Filter submenu items based on role permissions
  return filteredItems.map((item) => {
    if (item.items && item.items.length > 0) {
      return {
        ...item,
        items: item.items.filter((subItem) =>
          shouldShowSubmenuItem(subItem.link, user, account),
        ),
      };
    }
    return item;
  });
};
